import type { WaiterOnlineOrder } from './waiterApi';

/**
 * SC-961 — « En ligne » rows, most recently RECEIVED first. The list used to
 * follow spec §2.3 D7 (scheduledAt ascending, nulls last): service order, but
 * it put the order a customer had just placed at the very bottom, and the
 * waiter had to scroll the whole day to find it. Recency is measured on
 * `createdAt` — the moment the order arrived — not on the pickup slot the
 * customer asked for: a slot booked for 20:30 at noon is not "newer" than an
 * ASAP order placed a minute ago, and the ticket's pain is about spotting a
 * freshly-arrived order. The soonest pickup still has its own header line
 * (`findNextPickup`), so nothing is lost by not sorting on it.
 *
 * `dailyNumber` breaks a tie (two orders of the same second): descending too,
 * so the order is total and the rendered list never reshuffles between polls.
 */
export function compareOnlineOrdersRecentFirst(a: WaiterOnlineOrder, b: WaiterOnlineOrder): number {
  const delta = Date.parse(b.createdAt) - Date.parse(a.createdAt);
  if (delta !== 0) return delta;
  return b.dailyNumber - a.dailyNumber;
}

/** Non-mutating: returns a new array, most recent first. */
export function sortOnlineOrdersRecentFirst(orders: WaiterOnlineOrder[]): WaiterOnlineOrder[] {
  return [...orders].sort(compareOnlineOrdersRecentFirst);
}

/**
 * The soonest pickup still ahead of `now` — what the « Prochain retrait »
 * header names. It used to be `sortedOnlineOrders.find(...)`, which only held
 * while the list itself was sorted scheduledAt-ascending; with the list now
 * ordered by arrival it has to scan for the minimum itself. A late order is
 * skipped on purpose (review F3#7b): it already reads as late via
 * `OnlineOrderRow`'s red time, and "Prochain retrait" naming a time already
 * gone would read as a bug.
 */
export function findNextPickup(
  orders: WaiterOnlineOrder[],
  now: number = Date.now(),
): WaiterOnlineOrder | null {
  let best: WaiterOnlineOrder | null = null;
  let bestAt = Infinity;
  for (const order of orders) {
    if (order.scheduledAt === null) continue;
    const at = Date.parse(order.scheduledAt);
    if (at <= now || at >= bestAt) continue;
    best = order;
    bestAt = at;
  }
  return best;
}
